import { DialogComponent } from '@theme/dialog';

/**
 * Natalie try-on pilot (Oct 2026).
 *
 * The shopper picks or takes a photo, we prepare it in the browser and post it
 * to the GLAS server (campaigns app, /api/try-on/natalie), which edits it with
 * OpenAI. GLAS keeps the photo in memory only: no upload to Shopify, no storage.
 * The dialog carries `ph-no-capture` so PostHog session replay never records it.
 *
 * @extends DialogComponent
 */

// Must match ecommerce/landings/app/lib/tryon/size.ts in the campaigns repo.
const MULTIPLE = 16;
const MAX_PIXELS = 2560 * 1440;
const MAX_EDGE = 2560;
const MIN_PIXELS = 655360;
const MAX_ASPECT = 3;

/** @param {number} srcWidth @param {number} srcHeight */
function fitSize(srcWidth, srcHeight) {
  const aspect = Math.min(MAX_ASPECT, Math.max(1 / MAX_ASPECT, srcWidth / srcHeight));
  const area = Math.max(Math.min(srcWidth * srcHeight, MAX_PIXELS), MIN_PIXELS);
  let w = Math.sqrt(area * aspect);
  let h = Math.sqrt(area / aspect);
  const longest = Math.max(w, h);
  if (longest > MAX_EDGE) {
    w = (w * MAX_EDGE) / longest;
    h = (h * MAX_EDGE) / longest;
  }
  let width = Math.floor(w / MULTIPLE) * MULTIPLE;
  let height = Math.floor(h / MULTIPLE) * MULTIPLE;
  if (width * height < MIN_PIXELS) {
    width = Math.ceil(w / MULTIPLE) * MULTIPLE;
    height = Math.ceil(h / MULTIPLE) * MULTIPLE;
  }
  while (width > MAX_ASPECT * height) width -= MULTIPLE;
  while (height > MAX_ASPECT * width) height -= MULTIPLE;
  return { width, height };
}

/**
 * Upright (browsers apply EXIF orientation when drawing), scaled to the size
 * the model will return, re-encoded as JPEG 95. No face crop, no sharpening.
 * Re-encoding also drops EXIF, including GPS.
 * @param {File} file
 * @returns {Promise<Blob>}
 */
async function preparePhoto(file) {
  const src = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
    await img.decode();
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const out = fitSize(w, h);
    const canvas = document.createElement('canvas');
    canvas.width = out.width;
    canvas.height = out.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const scale = Math.max(out.width / w, out.height / h);
    const sw = out.width / scale;
    const sh = out.height / scale;
    ctx.drawImage(img, (w - sw) / 2, (h - sh) / 2, sw, sh, 0, 0, out.width, out.height);
    return await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob'))), 'image/jpeg', 0.95)
    );
  } finally {
    URL.revokeObjectURL(src);
  }
}

/** Daily cap reached: "fully booked, come back tomorrow". */
const BOOKED_CODES = new Set(['daily_cap', 'ip_cap']);
/** Our side is down (OpenAI out of credit or overloaded, counter or config missing): "try later". */
const DOWN_CODES = new Set(['busy', 'unavailable', 'not_configured']);

class NatalieTryOn extends DialogComponent {
  requiredRefs = ['dialog', 'body', 'go', 'consent', 'errorText', 'preview', 'previewFrame', 'loadingImage', 'resultImage'];

  /** @type {Blob | null} */
  #photo = null;
  /** @type {string | null} */
  #photoUrl = null;
  /** data: URL from the server. @type {string | null} */
  #resultUrl = null;
  #busy = false;

  /** Latest photo pick; an older pick that finishes later is ignored. */
  #pick = 0;

  get #endpoint() {
    return this.dataset.endpoint ?? '';
  }

  /** @param {'pick' | 'loading' | 'result' | 'error'} state */
  #setState(state) {
    this.refs.body.dataset.state = state;
  }

  /** The photo is sent only after the shopper has ticked the consent box. */
  #updateGo() {
    this.refs.go.disabled = !this.#photo || !this.refs.consent.checked;
  }

  onConsent = () => this.#updateGo();

  /** @param {string} code Server error code, or '' for a photo problem. */
  #showError(code) {
    const el = this.refs.errorText;
    const text = BOOKED_CODES.has(code) ? el.dataset.busy : DOWN_CODES.has(code) ? el.dataset.down : el.dataset.error;
    el.textContent = text ?? '';
    this.#setState('error');
  }

  openTryOn() {
    if (!this.#busy && !this.#resultUrl) this.#setState('pick');
    this.showDialog();
  }

  /** @param {Event} event */
  onFile = async (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    const { go, preview, previewFrame } = this.refs;
    const pick = ++this.#pick;
    go.disabled = true;
    try {
      const photo = await preparePhoto(file);
      if (pick !== this.#pick) return;
      this.#clearPhoto();
      this.#photo = photo;
      this.#photoUrl = URL.createObjectURL(photo);
      preview.src = this.#photoUrl;
      previewFrame.hidden = false;
      this.#updateGo();
      this.#setState('pick');
    } catch {
      // HEIC on desktop Chrome and broken files land here.
      if (pick === this.#pick) this.#showError('');
    }
  };

  generate = async () => {
    if (!this.#photo || !this.#photoUrl || this.#busy || !this.refs.consent.checked) return;
    const { loadingImage, resultImage } = this.refs;
    this.#busy = true;
    loadingImage.src = this.#photoUrl;
    this.#setState('loading');

    const body = new FormData();
    body.append('customerImage', this.#photo, 'photo.jpg');
    try {
      const res = await fetch(this.#endpoint, { method: 'POST', body });
      const json = await res.json();
      if (!res.ok || !json.ok || typeof json.image !== 'string') throw new Error(json.error || res.status);
      this.#clearResult();
      this.#resultUrl = json.image;
      resultImage.src = this.#resultUrl;
      this.showResult();
      this.#setState('result');
    } catch (error) {
      const code = error instanceof Error ? error.message : String(error);
      console.warn('[natalie-try-on]', code);
      this.#showError(code);
    } finally {
      this.#busy = false;
    }
  };

  showOriginal() {
    const { resultImage, showOriginalButton, showResultButton } = this.refs;
    if (this.#photoUrl) resultImage.src = this.#photoUrl;
    showOriginalButton?.setAttribute('aria-pressed', 'true');
    showResultButton?.setAttribute('aria-pressed', 'false');
  }

  showResult() {
    const { resultImage, showOriginalButton, showResultButton } = this.refs;
    if (this.#resultUrl) resultImage.src = this.#resultUrl;
    showOriginalButton?.setAttribute('aria-pressed', 'false');
    showResultButton?.setAttribute('aria-pressed', 'true');
  }

  retake() {
    this.#clearResult();
    this.#clearPhoto();
    this.refs.previewFrame.hidden = true;
    this.#updateGo();
    this.#setState('pick');
  }

  /** Back to step 1 with the same photo, so the shopper can try again or pick another. */
  retry() {
    this.#clearResult();
    this.#updateGo();
    this.#setState('pick');
  }

  continueToStrength = async () => {
    await this.closeDialog();
    const section = this.closest('.shopify-section') ?? document;
    const fieldset = section.querySelector('fieldset[data-is-strength="true"]');
    if (!fieldset) return;
    fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input =
      fieldset.querySelector('input:checked') ?? fieldset.querySelector('input:not([aria-disabled="true"])');
    if (input instanceof HTMLElement) input.focus({ preventScroll: true });
  };

  #clearPhoto() {
    if (this.#photoUrl) URL.revokeObjectURL(this.#photoUrl);
    this.#photoUrl = null;
    this.#photo = null;
    this.refs.preview.removeAttribute('src');
  }

  #clearResult() {
    this.#resultUrl = null;
    this.refs.resultImage.removeAttribute('src');
  }
}

if (!customElements.get('natalie-try-on')) customElements.define('natalie-try-on', NatalieTryOn);
